import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { PageTitle, PageLink } from '../../../rbts/layout/core';
import { LoadingGrid, formatPrice } from '../../../rbts/helpers';
import { styleSelect } from '../../../rbts/assets/ts/_utils';
import { useObtenerDiasPorEvento } from './cortesias/hooks/CortesiasHook';

const API_URL = process.env.REACT_APP_API;

const bgStyle = { backgroundColor: '#f0f1f5', boxShadow: 'none', border: 'none' };

const formatFecha = (fechaStr: string) => {
  if (!fechaStr) return '—';
  const d = new Date(fechaStr);
  return d.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function ReporteRetiros() {
  const [idDia, setIdDia] = useState<number>(0);
  const [diaSeleccionado, setDiaSeleccionado] = useState<any | null>(null);
  const [list, setList] = useState<any[]>([]);
  const [retirosDetalle, setRetirosDetalle] = useState<Record<string, any[]>>({});
  const [expandedCaja, setExpandedCaja] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [montoLimite, setMontoLimite] = useState<number>(0);

  const { list: listadosDeDias, loadingRequest: cargandoDias } = useObtenerDiasPorEvento(0);

  useEffect(() => {
    axios.get(`${API_URL}retiro/monto-limite`)
      .then(res => setMontoLimite(parseFloat(res.data.doc.data.monto_limite) || 0))
      .catch(() => {});
  }, []);

  const fetchReporte = async (diaId: number) => {
    if (!diaId) return;
    setLoading(true);
    setExpandedCaja(null);
    try {
      const [resReporte, resDetalle] = await Promise.all([
        axios.get(`${API_URL}retiro/reporte/${diaId}`),
        axios.get(`${API_URL}retiro/dia/${diaId}`),
      ]);

      setList(resReporte.data.doc.data || []);

      // Agrupa retiros individuales por nombre de caja
      const agrupado: Record<string, any[]> = {};
      const retiros: any[] = resDetalle.data.doc.data || [];
      retiros.forEach((r: any) => {
        const caja = r.punto_venta?.nombre ?? r.punto_venta ?? 'Sin nombre';
        if (!agrupado[caja]) agrupado[caja] = [];
        agrupado[caja].push(r);
      });
      setRetirosDetalle(agrupado);
    } catch (err) {
      toast.error('Error al obtener el reporte de retiros');
      setList([]);
      setRetirosDetalle({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (idDia > 0) {
      fetchReporte(idDia);
    } else {
      setList([]);
      setRetirosDetalle({});
      setExpandedCaja(null);
    }
  }, [idDia]);

  const widgetsBreadCrumbs: Array<PageLink> = [
    { title: 'Reportes', path: '/reportes/retiros', isSeparator: false, isActive: false },
    { title: '', path: '', isSeparator: true, isActive: false },
  ];

  const renderAlerta = (row: any) => {
    const total = row.total_real;
    if (montoLimite > 0 && total > montoLimite) {
      return (
        <span className="badge badge-light-danger fw-bold px-3 py-2 d-flex align-items-center gap-1">
          <i className="bi bi-circle-fill text-danger" style={{ fontSize: '10px' }} />
          Requiere retiro
        </span>
      );
    }
    if (montoLimite > 0 && total > montoLimite * 0.75) {
      return (
        <span className="badge badge-light-warning fw-bold px-3 py-2 d-flex align-items-center gap-1">
          <i className="bi bi-circle-fill text-warning" style={{ fontSize: '10px' }} />
          Cerca del límite
        </span>
      );
    }
    return (
      <span className="badge badge-light-success fw-bold px-3 py-2 d-flex align-items-center gap-1">
        <i className="bi bi-check-square-fill text-success" style={{ fontSize: '12px' }} />
        OK
      </span>
    );
  };

  return (
    <>
      <PageTitle breadcrumbs={widgetsBreadCrumbs}>Reporte de Retiros</PageTitle>

      {/* ── Filtros ─────────────────────────────────────────────────── */}
      <div className="card mb-5">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bolder fs-3 mb-1">Filtros de Reporte</span>
            <span className="text-muted mt-1 fw-bold fs-7">
              Seleccione el día administrativo para visualizar el reporte de retiros
            </span>
          </h3>
        </div>
        <div className="card-body py-3">
          <div className="row">
            <div className="col-lg-4">
              <label className="fw-bold fs-6 mb-2">Día Administrativo</label>
              <Select
                value={diaSeleccionado}
                isLoading={cargandoDias}
                options={listadosDeDias}
                styles={styleSelect}
                placeholder="Seleccione un Día Administrativo"
                onChange={(option: any) => {
                  setDiaSeleccionado(option);
                  setIdDia(option ? option.value : 0);
                }}
                isClearable
                noOptionsMessage={() => 'Sin opciones'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabla ───────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header border-0 pt-5">
          <div className="card-title">
            <h3 className="card-label fw-bolder text-dark">Reporte de Retiros</h3>
          </div>
        </div>
        <div className="card-body pt-3">
          {!idDia ? (
            <div className="text-center text-muted py-10 fs-6">
              Seleccione un día administrativo para ver el reporte.
            </div>
          ) : loading ? (
            <LoadingGrid text="Cargando reporte..." />
          ) : list.length === 0 ? (
            <div className="text-center text-muted py-10 fs-6">
              No se encontraron registros para el día seleccionado.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-rounded table-striped border gy-4 gs-7">
                <thead>
                  <tr className="fw-bolder fs-7 text-gray-800 border-bottom border-gray-200">
                    <th>Caja</th>
                    <th className="text-end">Monto Inicial</th>
                    <th className="text-end">Venta Acumulada</th>
                    <th className="text-end">Monto Retirado</th>
                    <th className="text-end">Monto Actual</th>
                    <th className="text-center">Alerta de Retiro</th>
                    <th>Fecha de Ingreso</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row: any, idx: number) => {
                    const isExpanded = expandedCaja === row.caja;
                    const detalle = retirosDetalle[row.caja] ?? [];
                    return (
                      <React.Fragment key={`${row.caja}-${idx}`}>
                        {/* Fila principal */}
                        <tr className="align-middle">
                          <td className="fs-7">
                            <button
                              className="btn btn-link p-0 fw-bold text-start d-flex align-items-center gap-1"
                              style={{ color: '#1e3a5f', textDecoration: 'none' }}
                              onClick={() => setExpandedCaja(isExpanded ? null : row.caja)}
                              title={isExpanded ? 'Ocultar detalle' : 'Ver detalle de retiros'}
                            >
                              <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} fs-7`} />
                              {row.caja}
                            </button>
                          </td>
                          <td className="text-end fs-7">
                            {formatPrice(row.inicio, 'es-MX', 'MXN', 2, 2)}
                          </td>
                          <td className="text-end fs-7">
                            {formatPrice(row.venta_acumulada, 'es-MX', 'MXN', 2, 2)}
                          </td>
                          <td className="text-end fs-7 fw-semibold" style={{ color: '#c0392b' }}>
                            {formatPrice(row.monto_retirado, 'es-MX', 'MXN', 2, 2)}
                          </td>
                          <td className="text-end fs-7 fw-bold" style={{ color: '#1a7a4a' }}>
                            {formatPrice(row.total_real, 'es-MX', 'MXN', 2, 2)}
                          </td>
                          <td className="text-center fs-7">{renderAlerta(row)}</td>
                          <td className="fs-7">
                            {new Date(row.fecha).toLocaleDateString('es-MX')}
                          </td>
                        </tr>

                        {/* Fila de detalle expandible */}
                        {isExpanded && (
                          <tr>
                            <td
                              colSpan={7}
                              style={{ padding: '4px 16px 12px 40px', backgroundColor: '#fafbfc' }}
                            >
                              {detalle.length === 0 ? (
                                <div className="text-muted fs-7 py-3 fst-italic">
                                  Sin retiros registrados para esta caja
                                </div>
                              ) : (
                                <table className="table table-sm mb-0 border" style={{ borderRadius: '6px', overflow: 'hidden' }}>
                                  <thead>
                                    <tr style={bgStyle}>
                                      <th className="fs-7 fw-bolder py-2 ps-3" style={{ color: '#1e3a5f' }}>Folio</th>
                                      <th className="fs-7 fw-bolder py-2" style={{ color: '#1e3a5f' }}>Usuario</th>
                                      <th className="fs-7 fw-bolder py-2 text-end" style={{ color: '#1e3a5f' }}>Monto Retirado</th>
                                      <th className="fs-7 fw-bolder py-2" style={{ color: '#1e3a5f' }}>Fecha / Hora</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detalle.map((r: any, i: number) => (
                                      <tr
                                        key={`det-${r.id}-${i}`}
                                        className="align-middle"
                                        style={{ borderBottom: '1px dashed #e4e6ef' }}
                                      >
                                        <td className="fs-7 py-2 ps-3 fw-semibold" style={{ color: '#1e3a5f' }}>
                                          {r.folio ?? '—'}
                                        </td>
                                        <td className="fs-7 py-2">{r.usuario ?? '—'}</td>
                                        <td className="fs-7 py-2 text-end fw-bold" style={{ color: '#c0392b' }}>
                                          {formatPrice(r.total_retiro ?? 0, 'es-MX', 'MXN', 2, 2)}
                                        </td>
                                        <td className="fs-7 py-2">{formatFecha(r.fecha)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
